import { DatumType, DatumValueType, GraphProperty, NodeId, Tag } from 'dashboard/models/graph';
import {
  ACCOUNT_DATUM_TYPE,
  ACCOUNT_SIMILARITY_SELFIE_DATUM_TYPE,
  ACCOUNT_STATUS_DATUM_TYPE,
  ACCOUNT_TYPE_DATUM_TYPE,
  ADDRESS_DATUM_TYPE,
  BIRTHDATE_DATUM_TYPE,
  BROWSER_FINGERPRINT_DATUM_TYPE,
  DEVICE_FINGERPRINT_BROWSER_DATUM_TYPE,
  DEVICE_FINGERPRINT_MOBILE_SDK_DATUM_TYPE,
  DEVICE_TOKEN_DATUM_TYPE,
  EMAIL_DATUM_TYPE,
  EXTERNAL_ACCOUNT_DATUM_TYPE,
  GOVERNMENT_ID_DATUM_TYPE,
  ID_DOCUMENT_NUMBER_DATUM_TYPE,
  ID_NUMBER_DATUM_TYPE,
  NAME_DATUM_TYPE,
  NETWORK_DATUM_TYPE,
  PHONE_NUMBER_DATUM_TYPE,
  POSTAL_CODE_DATUM_TYPE,
  SOCIAL_SECURITY_NUMBER_DATUM_TYPE,
  SOCIAL_SECURITY_NUMBER_LAST_4_DATUM_TYPE,
  TRANSACTIONS_DATUM_TYPE,
} from 'lib/constants/graph';
import { EmailSimCriteria, ImageBackgroundSimCriteria } from 'lib/constants/graph-datum-type';
import { Iso8601DateString } from 'lib/types';

export type AccountToken = string;
export type ReferenceId = string;
export type Address = string;
export type BrowserFingerprint = string;
export type DeviceFingerprintBrowser = string;
export type DeviceFingerprintMobileSdk = string;
export type EmailAddress = string;
export type IpAddress = string;
export type Name = string;
export type PhoneNumber = string;
export type PostalCode = string;

// NOTE: { id: string; filename: string } is to be deprecated for { token: string } as
// we are moving away from Active Storage to Shrine for saving selfies.
export type DemoBackendSelfieFile = { id: 'DemoBackend'; filename: string };
export type SelfieFile =
  | { id: string; filename: string }
  | { token: string }
  | DemoBackendSelfieFile;
export type Ssn = string;
export type SsnLast4 = string;

// --------------------------------------------------------------------------
// Transactions Demo interface
// --------------------------------------------------------------------------

// TODO(kerry): Figure out the correct abstraction to uniquely identify an account.
export interface TransactionAccountInfo {
  'account-number': string;
  'routing-number': string;
}

export interface TransactionEndpoint {
  'transaction-account-info'?: TransactionAccountInfo;
}

// --------------------------------------------------------------------------
// Accounts
// --------------------------------------------------------------------------

export interface ServerHydratedAccount extends TransactionEndpoint {
  token: AccountToken;
  'created-at': string;
  'reference-id': ReferenceId;
  status: string;
  tags: Tag[];
  name: {
    first: string;
    middle: string;
    last: string;
  };
  selfies: SelfieFile[];
  'inquiry-counts': {
    approved: number;
    declined: number;
    'needs-review': number;
    completed: number;
    failed: number;
    expired: number;
    created: number;
    pending: number;
  };
  'has-completed-inquiries': boolean;
  properties: ServerHydratedProperty[];
  'fields-data': {
    'account-type-token': string;
    'values-map': any;
  } | null;
}

export interface ServerHydratedProperty {
  'display-name': string;
  'property-key': GraphProperty;
  type: string;
  values: string[];
}

export interface HydratedAccount {
  nodeType: 'account';
  hydrationData: ServerHydratedAccount;
}

export interface ServerLightlyHydratedAccount extends TransactionEndpoint {
  status: string;
  tags: Tag[];
  'has-completed-inquiries': boolean;
}

export interface LightlyHydratedAccount {
  nodeType: 'account';
  hydrationData: ServerLightlyHydratedAccount;
}

// --------------------------------------------------------------------------
// Account similarity selfie
// --------------------------------------------------------------------------

export interface ServerHydratedAccountSimilaritySelfie {
  account1: {
    id: string;
    selfies: SelfieFile[];
  };
  account2: {
    id: string;
    selfies: SelfieFile[];
  };
  confidence: number;
}

export interface HydratedAccountSimilaritySelfie {
  nodeType: 'account_similarity_selfie';
  hydrationData: ServerHydratedAccountSimilaritySelfie;
}

export interface ServerLightlyHydratedAccountSimilaritySelfie {
  account1: {
    id: string;
  };
  account2: {
    id: string;
  };
}

export interface LightlyHydratedAccountSimilaritySelfie {
  nodeType: 'account_similarity_selfie';
  hydrationData: ServerLightlyHydratedAccountSimilaritySelfie;
}

// --------------------------------------------------------------------------
// Account statuses
// --------------------------------------------------------------------------

export interface ServerHydratedAccountStatus {
  status: string;
}

export interface HydratedAccountStatus {
  nodeType: 'account_status';
  hydrationData: ServerHydratedAccountStatus;
}

export interface ServerLightlyHydratedAccountStatus {}

export interface LightlyHydratedAccountStatus {
  nodeType: 'account_status';
  hydrationData: ServerLightlyHydratedAccountStatus;
}

// --------------------------------------------------------------------------
// Account types
// --------------------------------------------------------------------------

export interface ServerHydratedAccountType {
  type: string;
}

export interface HydratedAccountType {
  nodeType: 'account_type';
  hydrationData: ServerHydratedAccountType;
}

export interface ServerLightlyHydratedAccountType {}

export interface LightlyHydratedAccountType {
  nodeType: 'account_type';
  hydrationData: ServerLightlyHydratedAccountType;
}

// --------------------------------------------------------------------------
// Addresses
// --------------------------------------------------------------------------

export interface ServerHydratedAddress {
  'address-line1': Address;
  'address-line2': Address;
  'address-line3': Address;
  'country-code': string;
}

export interface HydratedAddress {
  nodeType: 'address';
  hydrationData: ServerHydratedAddress;
}

export interface ServerLightlyHydratedAddress {}

export interface LightlyHydratedAddress {
  nodeType: 'address';
  hydrationData: ServerLightlyHydratedAddress;
}

// --------------------------------------------------------------------------
// Birthdates
// --------------------------------------------------------------------------

export interface ServerHydratedBirthdate {
  birthdate: Iso8601DateString;
}

export interface HydratedBirthdate {
  nodeType: 'birthdate';
  hydrationData: ServerHydratedBirthdate;
}

export interface ServerLightlyHydratedBirthdate {}

export interface LightlyHydratedBirthdate {
  nodeType: 'birthdate';
  hydrationData: ServerLightlyHydratedBirthdate;
}

// --------------------------------------------------------------------------
// Browser fingerprints
// --------------------------------------------------------------------------

export interface ServerHydratedBrowserFingerprint {
  'browser-fingerprint': BrowserFingerprint;
  'browser-names': string[];
}

export interface HydratedBrowserFingerprint {
  nodeType: 'browser_fingerprint';
  hydrationData: ServerHydratedBrowserFingerprint;
}

export interface ServerLightlyHydratedBrowserFingerprint {}

export interface LightlyHydratedBrowserFingerprint {
  nodeType: 'browser_fingerprint';
  hydrationData: ServerLightlyHydratedBrowserFingerprint;
}

// --------------------------------------------------------------------------
// Device fingerprints
// --------------------------------------------------------------------------

export interface ServerHydratedDeviceFingerprint {
  'device-fingerprint': DeviceFingerprintBrowser;
  'device-types': string[];
}

export interface HydratedDeviceFingerprint {
  nodeType: 'device_fingerprint';
  hydrationData: ServerHydratedDeviceFingerprint;
}

export interface ServerLightlyHydratedDeviceFingerprint {}

export interface LightlyHydratedDeviceFingerprint {
  nodeType: 'device_fingerprint';
  hydrationData: ServerLightlyHydratedDeviceFingerprint;
}

// --------------------------------------------------------------------------
// Device tokens
// --------------------------------------------------------------------------

export interface ServerHydratedDeviceToken {
  token: string;
}

export interface HydratedDeviceToken {
  nodeType: 'device_token';
  hydrationData: ServerHydratedDeviceToken;
}

export interface ServerLightlyHydratedDeviceToken {}

export interface LightlyHydratedDeviceToken {
  nodeType: 'device_token';
  hydrationData: ServerLightlyHydratedDeviceToken;
}

// --------------------------------------------------------------------------
// Device vendor IDs
// --------------------------------------------------------------------------

export interface ServerHydratedDeviceVendorId {
  'device-vendor-id': DeviceFingerprintMobileSdk;
}

export interface HydratedDeviceVendorId {
  nodeType: 'device_vendor_id';
  hydrationData: ServerHydratedDeviceVendorId;
}

export interface ServerLightlyHydratedDeviceVendorId {}

export interface LightlyHydratedDeviceVendorId {
  nodeType: 'device_vendor_id';
  hydrationData: ServerLightlyHydratedDeviceVendorId;
}

// --------------------------------------------------------------------------
// Emails
// --------------------------------------------------------------------------

export interface ServerHydratedEmail {
  email: EmailAddress;
}

export interface HydratedEmail {
  nodeType: 'email';
  hydrationData: ServerHydratedEmail;
}

export interface ServerLightlyHydratedEmail {}

export interface LightlyHydratedEmail {
  nodeType: 'email';
  hydrationData: ServerLightlyHydratedEmail;
}

// --------------------------------------------------------------------------
// Government IDs
// --------------------------------------------------------------------------

export interface ServerHydratedGovernmentId {
  id: string;
}

export interface HydratedGovernmentId {
  nodeType: 'government_id';
  hydrationData: ServerHydratedGovernmentId;
}

export interface ServerLightlyHydratedGovernmentId {}

export interface LightlyHydratedGovernmentId {
  nodeType: 'government_id';
  hydrationData: ServerLightlyHydratedGovernmentId;
}

// --------------------------------------------------------------------------
// ID document numbers
// --------------------------------------------------------------------------

export interface ServerHydratedIdDocumentNumber {
  'document-number': string;
  'issuing-country': string;
  'issuing-subdivision': string;
}

export interface HydratedIdDocumentNumber {
  nodeType: 'id_document_number';
  hydrationData: ServerHydratedIdDocumentNumber;
}

export interface ServerLightlyHydratedIdDocumentNumber {}

export interface LightlyHydratedIdDocumentNumber {
  nodeType: 'id_document_number';
  hydrationData: ServerLightlyHydratedIdDocumentNumber;
}

// --------------------------------------------------------------------------
// ID numbers
// --------------------------------------------------------------------------

export interface ServerHydratedIdNumber {
  id: string;
}

export interface HydratedIdNumber {
  nodeType: 'id_number';
  hydrationData: ServerHydratedIdNumber;
}

export interface ServerLightlyHydratedIdNumber {}

export interface LightlyHydratedIdNumber {
  nodeType: 'id_number';
  hydrationData: ServerLightlyHydratedIdNumber;
}

// --------------------------------------------------------------------------
// Names
// --------------------------------------------------------------------------

export interface ServerHydratedName {
  'name-first': Name;
  'name-middle': Name;
  'name-last': Name;
}

export interface HydratedName {
  nodeType: 'name';
  hydrationData: ServerHydratedName;
}

export interface ServerLightlyHydratedName {}

export interface LightlyHydratedName {
  nodeType: 'name';
  hydrationData: ServerLightlyHydratedName;
}

// --------------------------------------------------------------------------
// Networks
// --------------------------------------------------------------------------

export interface ServerHydratedNetwork {
  ip: IpAddress;
  'proxy-type': string;
  'threat-level': string;
  'region-name': string;
  'country-name': string;
}

export interface HydratedNetwork {
  nodeType: 'network';
  hydrationData: ServerHydratedNetwork;
}

export interface ServerLightlyHydratedNetwork {}

export interface LightlyHydratedNetwork {
  nodeType: 'network';
  hydrationData: ServerLightlyHydratedNetwork;
}

// --------------------------------------------------------------------------
// Phone numbers
// --------------------------------------------------------------------------

export interface ServerHydratedPhoneNumber {
  phone: PhoneNumber;
}

export interface HydratedPhoneNumber {
  nodeType: 'phone';
  hydrationData: ServerHydratedPhoneNumber;
}

export interface ServerLightlyHydratedPhoneNumber {}

export interface LightlyHydratedPhoneNumber {
  nodeType: 'phone';
  hydrationData: ServerLightlyHydratedPhoneNumber;
}

// --------------------------------------------------------------------------
// Postal codes
// --------------------------------------------------------------------------

export interface ServerHydratedPostalCode {
  'postal-code': PostalCode;
}

export interface HydratedPostalCode {
  nodeType: 'postal_code';
  hydrationData: ServerHydratedPostalCode;
}

export interface ServerLightlyHydratedPostalCode {}

export interface LightlyHydratedPostalCode {
  nodeType: 'postal_code';
  hydrationData: ServerLightlyHydratedPostalCode;
}

// --------------------------------------------------------------------------
// Selfie similarity
// --------------------------------------------------------------------------

export interface ServerHydratedSelfieSimilarity {
  'account-selfies': {
    'account-token': AccountToken;
    selfies: SelfieFile[];
  }[];
  confidence: number;
}

export interface HydratedSelfieSimilarity {
  nodeType: 'selfie_similarity';
  hydrationData: ServerHydratedSelfieSimilarity;
}

export interface ServerLightlyHydratedSelfieSimilarity {}

export interface LightlyHydratedSelfieSimilarity {
  nodeType: 'selfie_similarity';
  hydrationData: ServerLightlyHydratedSelfieSimilarity;
}

// --------------------------------------------------------------------------
// SSNs
// --------------------------------------------------------------------------

export interface ServerHydratedSsn {
  ssn: Ssn;
}

export interface HydratedSsn {
  nodeType: 'ssn';
  hydrationData: ServerHydratedSsn;
}

export interface ServerLightlyHydratedSsn {}

export interface LightlyHydratedSsn {
  nodeType: 'ssn';
  hydrationData: ServerLightlyHydratedSsn;
}

// --------------------------------------------------------------------------
// SSN last 4
// --------------------------------------------------------------------------

export interface ServerHydratedSsnLast4 {
  'ssn-last4': SsnLast4;
}

export interface HydratedSsnLast4 {
  nodeType: 'ssn_last4';
  hydrationData: ServerHydratedSsnLast4;
}

export interface ServerLightlyHydratedSsnLast4 {}

export interface LightlyHydratedSsnLast4 {
  nodeType: 'ssn_last4';
  hydrationData: ServerLightlyHydratedSsnLast4;
}

// --------------------------------------------------------------------------
// Transactions
// --------------------------------------------------------------------------

// Modelling an account that's external to Persona, since we probably want to track transactions that flow
// out of the customer accounts.
export interface ServerHydratedExternalAccount extends TransactionEndpoint {}

export interface HydratedExternalAccount {
  nodeType: 'external_account';
  hydrationData: ServerHydratedExternalAccount;
}

export interface ServerLightlyHydratedExternalAccount extends TransactionEndpoint {}

export interface LightlyHydratedExternalAccount {
  nodeType: 'external_account';
  hydrationData: ServerLightlyHydratedExternalAccount;
}

export interface TransactionData {
  // The transaction ID.
  id: string;
  // We use a string here, because we don't want to be restricted to Javascript's number precision.
  // Positive amounts represent funds flowing from `account1` to `account2`, whereas negative amounts
  // present funds flowing from `account2` to `account1`.
  amount: string;
  currency: string;
  // TODO(kerry): Add other data fields here.
}

export interface ServerHydratedTransactions {
  // To make transactions between two account unique, we require: account1 <= account2, lexicographically.
  account1: TransactionAccountInfo;
  account2: TransactionAccountInfo;
  transactions: TransactionData[];
  // A scalar, representing the total volume moved from account1 to account2, scaled by the relative currency
  // values of each transaction. Used for determining the relative size of transactions visually on the canvas.
  netVolume: number;
}

export interface HydratedTransactions {
  nodeType: 'transactions';
  hydrationData: ServerHydratedTransactions;
}

export interface ServerLightlyHydratedTransactions {
  // To make transactions between two account unique, we require: account1 <= account2, lexicographically.
  account1: TransactionAccountInfo;
  account2: TransactionAccountInfo;
  // A scalar, representing the total volume moved from account1 to account2, scaled by the relative currency
  // values of each transaction. Used for determining the relative size of transactions visually on the canvas.
  netVolume: number;
}

export interface LightlyHydratedTransactions {
  nodeType: 'transactions';
  hydrationData: ServerLightlyHydratedTransactions;
}

// --------------------------------------------------------------------------
// Custom datum types
// --------------------------------------------------------------------------

export interface ServerHydratedCustomDatumType {
  // TODO(kerry): Make this payload more extensible.
  value: string;
}

export interface HydratedCustomDatumType {
  nodeType: 'custom_datum_type';
  hydrationData: ServerHydratedCustomDatumType;
}

export interface ServerLightlyHydratedCustomDatumType {}

export interface LightlyHydratedCustomDatumType {
  nodeType: 'custom_datum_type';
  hydrationData: ServerLightlyHydratedCustomDatumType;
}

// --------------------------------------------------------------------------
// Similarity
// --------------------------------------------------------------------------

// Email Similarity
export interface ServerHydratedEmailSim {
  'display-name': string;
  criteria: EmailSimCriteria[];
  details: {
    account1: {
      id: string;
      email: string; // TODO(jes) Handle multiple match criteria
    };
    account2: {
      id: string;
      email: string;
    };
  };
}

export interface HydratedEmailSim {
  nodeType: DatumValueType.EmailSimilarity;
  hydrationData: ServerHydratedEmailSim;
}

export interface ServerLightlyHydratedEmailSim {}

export interface LightlyHydratedEmailSim {
  nodeType: DatumValueType.EmailSimilarity;
  hydrationData: ServerLightlyHydratedEmailSim;
}

// Image Background Similarity
export interface ServerHydratedImageBackgroundSim {
  'display-name': string;
  criteria: ImageBackgroundSimCriteria[];
  details: {
    account1: {
      id: string;
      images: SelfieFile[];
    };
    account2: {
      id: string;
      images: SelfieFile[];
    };
    similarityScore: number;
  };
}

export interface HydratedImageBackgroundSim {
  nodeType: DatumValueType.ImageBackgroundSimilarity;
  hydrationData: ServerHydratedImageBackgroundSim;
}

export interface ServerLightlyHydratedImageBackgroundSim {}

export interface LightlyHydratedImageBackgroundSim {
  nodeType: DatumValueType.ImageBackgroundSimilarity;
  hydrationData: ServerLightlyHydratedImageBackgroundSim;
}

// --------------------------------------------------------------------------
// Union types
// --------------------------------------------------------------------------

export type ServerHydratedNode =
  | ServerHydratedAccount
  | ServerHydratedAccountSimilaritySelfie
  | ServerHydratedAccountStatus
  | ServerHydratedAccountType
  | ServerHydratedAddress
  | ServerHydratedBirthdate
  | ServerHydratedBrowserFingerprint
  | ServerHydratedDeviceFingerprint
  | ServerHydratedDeviceToken
  | ServerHydratedDeviceVendorId
  | ServerHydratedEmail
  | ServerHydratedEmailSim
  | ServerHydratedGovernmentId
  | ServerHydratedIdDocumentNumber
  | ServerHydratedIdNumber
  | ServerHydratedImageBackgroundSim
  | ServerHydratedName
  | ServerHydratedNetwork
  | ServerHydratedPhoneNumber
  | ServerHydratedPostalCode
  | ServerHydratedSelfieSimilarity
  | ServerHydratedSsn
  | ServerHydratedSsnLast4
  | ServerHydratedExternalAccount
  | ServerHydratedTransactions
  | ServerHydratedCustomDatumType;

export type HydratedNode =
  | HydratedAccount
  | HydratedAccountSimilaritySelfie
  | HydratedAccountStatus
  | HydratedAccountType
  | HydratedAddress
  | HydratedBirthdate
  | HydratedBrowserFingerprint
  | HydratedDeviceFingerprint
  | HydratedDeviceToken
  | HydratedDeviceVendorId
  | HydratedEmail
  | HydratedEmailSim
  | HydratedGovernmentId
  | HydratedIdDocumentNumber
  | HydratedIdNumber
  | HydratedImageBackgroundSim
  | HydratedName
  | HydratedNetwork
  | HydratedPhoneNumber
  | HydratedPostalCode
  | HydratedSelfieSimilarity
  | HydratedSsn
  | HydratedSsnLast4
  | HydratedExternalAccount
  | HydratedTransactions
  | HydratedCustomDatumType;

export type ServerLightlyHydratedNode =
  | ServerLightlyHydratedAccount
  | ServerLightlyHydratedAccountSimilaritySelfie
  | ServerLightlyHydratedAccountStatus
  | ServerLightlyHydratedAccountType
  | ServerLightlyHydratedAddress
  | ServerLightlyHydratedBirthdate
  | ServerLightlyHydratedBrowserFingerprint
  | ServerLightlyHydratedDeviceFingerprint
  | ServerLightlyHydratedDeviceToken
  | ServerLightlyHydratedDeviceVendorId
  | ServerLightlyHydratedEmail
  | ServerLightlyHydratedEmailSim
  | ServerLightlyHydratedGovernmentId
  | ServerLightlyHydratedIdDocumentNumber
  | ServerLightlyHydratedIdNumber
  | ServerLightlyHydratedImageBackgroundSim
  | ServerLightlyHydratedName
  | ServerLightlyHydratedNetwork
  | ServerLightlyHydratedPhoneNumber
  | ServerLightlyHydratedPostalCode
  | ServerLightlyHydratedSelfieSimilarity
  | ServerLightlyHydratedSsn
  | ServerLightlyHydratedSsnLast4
  | ServerLightlyHydratedExternalAccount
  | ServerLightlyHydratedTransactions
  | ServerLightlyHydratedCustomDatumType;

export type LightlyHydratedNode =
  | LightlyHydratedAccount
  | LightlyHydratedAccountSimilaritySelfie
  | LightlyHydratedAccountStatus
  | LightlyHydratedAccountType
  | LightlyHydratedAddress
  | LightlyHydratedBirthdate
  | LightlyHydratedBrowserFingerprint
  | LightlyHydratedDeviceFingerprint
  | LightlyHydratedDeviceToken
  | LightlyHydratedDeviceVendorId
  | LightlyHydratedEmail
  | LightlyHydratedEmailSim
  | LightlyHydratedGovernmentId
  | LightlyHydratedIdDocumentNumber
  | LightlyHydratedIdNumber
  | LightlyHydratedImageBackgroundSim
  | LightlyHydratedName
  | LightlyHydratedNetwork
  | LightlyHydratedPhoneNumber
  | LightlyHydratedPostalCode
  | LightlyHydratedSelfieSimilarity
  | LightlyHydratedSsn
  | LightlyHydratedSsnLast4
  | LightlyHydratedExternalAccount
  | LightlyHydratedTransactions
  | LightlyHydratedCustomDatumType;

// --------------------------------------------------------------------------
// Conversion functions
// --------------------------------------------------------------------------

export const convertToHydratedNode = (
  datumType: DatumType,
  serverHydratedNode: ServerHydratedNode,
): HydratedNode => {
  switch (datumType.token) {
    case ACCOUNT_DATUM_TYPE.token: {
      return {
        nodeType: 'account',
        hydrationData: serverHydratedNode as ServerHydratedAccount,
      };
    }
    case ACCOUNT_SIMILARITY_SELFIE_DATUM_TYPE.token: {
      return {
        nodeType: 'account_similarity_selfie',
        hydrationData: serverHydratedNode as ServerHydratedAccountSimilaritySelfie,
      };
    }
    case ACCOUNT_STATUS_DATUM_TYPE.token: {
      return {
        nodeType: 'account_status',
        hydrationData: serverHydratedNode as ServerHydratedAccountStatus,
      };
    }
    case ACCOUNT_TYPE_DATUM_TYPE.token: {
      return {
        nodeType: 'account_type',
        hydrationData: serverHydratedNode as ServerHydratedAccountType,
      };
    }
    case ADDRESS_DATUM_TYPE.token: {
      return {
        nodeType: 'address',
        hydrationData: serverHydratedNode as ServerHydratedAddress,
      };
    }
    case BIRTHDATE_DATUM_TYPE.token: {
      return {
        nodeType: 'birthdate',
        hydrationData: serverHydratedNode as ServerHydratedBirthdate,
      };
    }
    case BROWSER_FINGERPRINT_DATUM_TYPE.token: {
      return {
        nodeType: 'browser_fingerprint',
        hydrationData: serverHydratedNode as ServerHydratedBrowserFingerprint,
      };
    }
    case DEVICE_FINGERPRINT_BROWSER_DATUM_TYPE.token: {
      return {
        nodeType: 'device_fingerprint',
        hydrationData: serverHydratedNode as ServerHydratedDeviceFingerprint,
      };
    }
    case DEVICE_FINGERPRINT_MOBILE_SDK_DATUM_TYPE.token: {
      return {
        nodeType: 'device_vendor_id',
        hydrationData: serverHydratedNode as ServerHydratedDeviceVendorId,
      };
    }
    case DEVICE_TOKEN_DATUM_TYPE.token: {
      return {
        nodeType: 'device_token',
        hydrationData: serverHydratedNode as ServerHydratedDeviceToken,
      };
    }
    case EMAIL_DATUM_TYPE.token: {
      return {
        nodeType: 'email',
        hydrationData: serverHydratedNode as ServerHydratedEmail,
      };
    }
    case GOVERNMENT_ID_DATUM_TYPE.token: {
      return {
        nodeType: 'government_id',
        hydrationData: serverHydratedNode as ServerHydratedGovernmentId,
      };
    }
    case ID_DOCUMENT_NUMBER_DATUM_TYPE.token: {
      return {
        nodeType: 'id_document_number',
        hydrationData: serverHydratedNode as ServerHydratedIdDocumentNumber,
      };
    }
    case ID_NUMBER_DATUM_TYPE.token: {
      return {
        nodeType: 'id_number',
        hydrationData: serverHydratedNode as ServerHydratedIdNumber,
      };
    }
    case NAME_DATUM_TYPE.token: {
      return {
        nodeType: 'name',
        hydrationData: serverHydratedNode as ServerHydratedName,
      };
    }
    case NETWORK_DATUM_TYPE.token: {
      return {
        nodeType: 'network',
        hydrationData: serverHydratedNode as ServerHydratedNetwork,
      };
    }
    case PHONE_NUMBER_DATUM_TYPE.token: {
      return {
        nodeType: 'phone',
        hydrationData: serverHydratedNode as ServerHydratedPhoneNumber,
      };
    }
    case POSTAL_CODE_DATUM_TYPE.token: {
      return {
        nodeType: 'postal_code',
        hydrationData: serverHydratedNode as ServerHydratedPostalCode,
      };
    }
    case SOCIAL_SECURITY_NUMBER_DATUM_TYPE.token: {
      return {
        nodeType: 'ssn',
        hydrationData: serverHydratedNode as ServerHydratedSsn,
      };
    }
    case SOCIAL_SECURITY_NUMBER_LAST_4_DATUM_TYPE.token: {
      return {
        nodeType: 'ssn_last4',
        hydrationData: serverHydratedNode as ServerHydratedSsnLast4,
      };
    }
    case EXTERNAL_ACCOUNT_DATUM_TYPE.token: {
      return {
        nodeType: 'external_account',
        hydrationData: serverHydratedNode as ServerHydratedExternalAccount,
      };
    }
    case TRANSACTIONS_DATUM_TYPE.token: {
      return {
        nodeType: 'transactions',
        hydrationData: serverHydratedNode as ServerHydratedTransactions,
      };
    }
    default: {
      if (datumType.isCustomType) {
        switch (datumType.valueType) {
          case DatumValueType.Address:
            return {
              nodeType: 'address',
              hydrationData: serverHydratedNode as ServerHydratedAddress,
            };
          case DatumValueType.Email:
            return {
              nodeType: 'email',
              hydrationData: serverHydratedNode as ServerHydratedEmail,
            };
          case DatumValueType.EmailSimilarity:
            return {
              nodeType: DatumValueType.EmailSimilarity,
              hydrationData: serverHydratedNode as ServerHydratedEmailSim,
            };
          case DatumValueType.GovernmentId:
            return {
              nodeType: 'government_id',
              hydrationData: serverHydratedNode as ServerHydratedGovernmentId,
            };
          case DatumValueType.ImageBackgroundSimilarity:
            return {
              nodeType: DatumValueType.ImageBackgroundSimilarity,
              hydrationData: serverHydratedNode as ServerHydratedImageBackgroundSim,
            };
          case DatumValueType.Name:
            return {
              nodeType: 'name',
              hydrationData: serverHydratedNode as ServerHydratedName,
            };
          case DatumValueType.PhoneNumber:
            return {
              nodeType: 'phone',
              hydrationData: serverHydratedNode as ServerHydratedPhoneNumber,
            };
          case DatumValueType.PostalCode:
            return {
              nodeType: 'postal_code',
              hydrationData: serverHydratedNode as ServerHydratedPostalCode,
            };
          case DatumValueType.SocialSecurityNumber:
            return {
              nodeType: 'ssn',
              hydrationData: serverHydratedNode as ServerHydratedSsn,
            };
          case DatumValueType.SocialSecurityNumberLast4:
            return {
              nodeType: 'ssn_last4',
              hydrationData: serverHydratedNode as ServerHydratedSsnLast4,
            };
          default:
            return {
              nodeType: 'custom_datum_type',
              hydrationData: serverHydratedNode as ServerHydratedCustomDatumType,
            };
        }
      }
      throw new Error('unknown datum type');
    }
  }
};

export const convertToLightlyHydratedNode = (
  datumType: DatumType,
  serverLightlyHydratedNode: ServerLightlyHydratedNode,
): LightlyHydratedNode => {
  switch (datumType.token) {
    case ACCOUNT_DATUM_TYPE.token: {
      return {
        nodeType: 'account',
        hydrationData: serverLightlyHydratedNode as ServerLightlyHydratedAccount,
      };
    }
    case ACCOUNT_SIMILARITY_SELFIE_DATUM_TYPE.token: {
      return {
        nodeType: 'account_similarity_selfie',
        hydrationData: serverLightlyHydratedNode as ServerLightlyHydratedAccountSimilaritySelfie,
      };
    }
    case ACCOUNT_STATUS_DATUM_TYPE.token: {
      return {
        nodeType: 'account_status',
        hydrationData: serverLightlyHydratedNode as ServerLightlyHydratedAccountStatus,
      };
    }
    case ACCOUNT_TYPE_DATUM_TYPE.token: {
      return {
        nodeType: 'account_type',
        hydrationData: serverLightlyHydratedNode as ServerLightlyHydratedAccountType,
      };
    }
    case ADDRESS_DATUM_TYPE.token: {
      return {
        nodeType: 'address',
        hydrationData: serverLightlyHydratedNode as ServerLightlyHydratedAddress,
      };
    }
    case BIRTHDATE_DATUM_TYPE.token: {
      return {
        nodeType: 'birthdate',
        hydrationData: serverLightlyHydratedNode as ServerLightlyHydratedBirthdate,
      };
    }
    case BROWSER_FINGERPRINT_DATUM_TYPE.token: {
      return {
        nodeType: 'browser_fingerprint',
        hydrationData: serverLightlyHydratedNode as ServerLightlyHydratedBrowserFingerprint,
      };
    }
    case DEVICE_FINGERPRINT_BROWSER_DATUM_TYPE.token: {
      return {
        nodeType: 'device_fingerprint',
        hydrationData: serverLightlyHydratedNode as ServerLightlyHydratedDeviceFingerprint,
      };
    }
    case DEVICE_FINGERPRINT_MOBILE_SDK_DATUM_TYPE.token: {
      return {
        nodeType: 'device_vendor_id',
        hydrationData: serverLightlyHydratedNode as ServerLightlyHydratedDeviceVendorId,
      };
    }
    case DEVICE_TOKEN_DATUM_TYPE.token: {
      return {
        nodeType: 'device_token',
        hydrationData: serverLightlyHydratedNode as ServerLightlyHydratedDeviceToken,
      };
    }
    case EMAIL_DATUM_TYPE.token: {
      return {
        nodeType: 'email',
        hydrationData: serverLightlyHydratedNode as ServerLightlyHydratedEmail,
      };
    }
    case GOVERNMENT_ID_DATUM_TYPE.token: {
      return {
        nodeType: 'government_id',
        hydrationData: serverLightlyHydratedNode as ServerLightlyHydratedGovernmentId,
      };
    }
    case ID_DOCUMENT_NUMBER_DATUM_TYPE.token: {
      return {
        nodeType: 'id_document_number',
        hydrationData: serverLightlyHydratedNode as ServerLightlyHydratedIdDocumentNumber,
      };
    }
    case ID_NUMBER_DATUM_TYPE.token: {
      return {
        nodeType: 'id_number',
        hydrationData: serverLightlyHydratedNode as ServerLightlyHydratedIdNumber,
      };
    }
    case NAME_DATUM_TYPE.token: {
      return {
        nodeType: 'name',
        hydrationData: serverLightlyHydratedNode as ServerLightlyHydratedName,
      };
    }
    case NETWORK_DATUM_TYPE.token: {
      return {
        nodeType: 'network',
        hydrationData: serverLightlyHydratedNode as ServerLightlyHydratedNetwork,
      };
    }
    case PHONE_NUMBER_DATUM_TYPE.token: {
      return {
        nodeType: 'phone',
        hydrationData: serverLightlyHydratedNode as ServerLightlyHydratedPhoneNumber,
      };
    }
    case POSTAL_CODE_DATUM_TYPE.token: {
      return {
        nodeType: 'postal_code',
        hydrationData: serverLightlyHydratedNode as ServerLightlyHydratedPostalCode,
      };
    }
    case SOCIAL_SECURITY_NUMBER_DATUM_TYPE.token: {
      return {
        nodeType: 'ssn',
        hydrationData: serverLightlyHydratedNode as ServerLightlyHydratedSsn,
      };
    }
    case SOCIAL_SECURITY_NUMBER_LAST_4_DATUM_TYPE.token: {
      return {
        nodeType: 'ssn_last4',
        hydrationData: serverLightlyHydratedNode as ServerLightlyHydratedSsnLast4,
      };
    }
    case EXTERNAL_ACCOUNT_DATUM_TYPE.token: {
      return {
        nodeType: 'external_account',
        hydrationData: serverLightlyHydratedNode as ServerLightlyHydratedExternalAccount,
      };
    }
    case TRANSACTIONS_DATUM_TYPE.token: {
      return {
        nodeType: 'transactions',
        hydrationData: serverLightlyHydratedNode as ServerLightlyHydratedTransactions,
      };
    }
    default: {
      if (datumType.isCustomType) {
        switch (datumType.valueType) {
          case DatumValueType.EmailSimilarity:
            return {
              nodeType: DatumValueType.EmailSimilarity,
              hydrationData: serverLightlyHydratedNode as ServerLightlyHydratedEmailSim,
            };
          case DatumValueType.ImageBackgroundSimilarity:
            return {
              nodeType: DatumValueType.ImageBackgroundSimilarity,
              hydrationData: serverLightlyHydratedNode as ServerLightlyHydratedImageBackgroundSim,
            };
          default:
            return {
              nodeType: 'custom_datum_type',
              hydrationData: serverLightlyHydratedNode as ServerLightlyHydratedCustomDatumType,
            };
        }
      }
      throw new Error('unknown datum type');
    }
  }
};

export const convertHydratedNodeToLightlyHydratedNode = (
  hydratedNode: HydratedNode,
): LightlyHydratedNode => {
  // for now, assume lightlyHydratedNodes are simply subsets of hydratedNodes,
  // so we can just use hydratedNodes as lightlyHydratedNodes
  return hydratedNode;
};

export const extractTransactionsInfo = (
  lightlyHydratedNode: LightlyHydratedNode,
): TransactionAccountInfo => {
  switch (lightlyHydratedNode.nodeType) {
    case 'account':
    case 'external_account':
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      return lightlyHydratedNode.hydrationData['transaction-account-info'];
    default:
      throw new Error('Expected an account (internal or external)');
  }
};

export interface NodeIdAndLightHydration {
  nodeId: NodeId;
  lightHydration: LightlyHydratedNode;
}
